import { RootState } from "redux/store";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Card, Row, Col, Nav, Tab, Dropdown, Modal } from "react-bootstrap";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { updateMainGraph } from "redux/slices/Dashboard";
import { eChartColours, getMainColors } from "constants/colors";
import { EChartsOption } from "echarts";
import ReactECharts from 'echarts-for-react';
import EChartsReact from "echarts-for-react";
import { onlyUnique } from "helpers/Functions";
import { useNavigate } from "react-router";
import { FormatDateToDMY } from "helpers/data/dataHandling";
import { useSearchParams } from "react-router-dom";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZTooltip } from "components/ZTooltip";
import { format, formatISO } from "date-fns";
import { Dictionary } from "@reduxjs/toolkit/dist/entities/models";
import { ReportIds } from "constants/reportIds";

const TotalRevenueChart = () => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const colors = getMainColors();
    const navigate = useNavigate();
    const drilldownChartRef = useRef<ReactECharts>(null);
    const dashboard = useAppSelector((state: RootState) => state.dashboard);
    const stores = useAppSelector((state: RootState) => state.stores);
    const [drillDownOpen, setDrillDownOpen] = useState(false);
    const [drillDownParams, setDrillDownParams] = useState<{
        name: string,
        seriesName: string,
        value: number,
        tab: string,
        dataIx: number,
    } | undefined>();
    let isStacked = true; // Track whether the drilldown chart is stacked

    const years = useMemo(() => {
        return dashboard.comparisonGraph.data.toReversed().map((p) => p.name);
    }, [dashboard.comparisonGraph.data]);

    const chartOptions = useMemo<EChartsOption>(() => ({
        color: eChartColours,
        grid: {
            left: '3%',
            right: '3%',
            bottom: 40,
            top: '10%',
            containLabel: true,
            height: 'auto',
            width: 'auto',
        },
        animationDuration: 300,
        xAxis: {
            data: dashboard.comparisonGraph.axis,
            type: 'category',
            axisPointer: {
                show: true,
                type: 'shadow',
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (value: number) => {
                    if (value == null) return "N/A";
                    return ["Sales", "Profit"].includes(dashboard.mainGraph) ? value.formatAsPrice(false, true, true) : value.formatAsPrice(false, false, !["Refunds", "Scripts"].includes(dashboard.mainGraph));
                }
            }
        },
        series: dashboard.comparisonGraph.data.toReversed().map((p) => ({
            data: p.data.map(d => {
                const value = d != null ? Number.parseFloat(d.toFixed(2)) : undefined;
                return {
                    value: value,
                };
            }),
            barGap: '0%',
            type: 'bar',
            name: p.name,
        })),
        legend: {
            data: dashboard.comparisonGraph.data.map((p) => p.name).reverse(),
            formatter: (name: string) => {
                const totals = dashboard.comparisonGraph.data.find((p) => p.name === name)?.totals ?? 0;
                const formattedTotals = ["Sales", "Profit"].includes(dashboard.mainGraph) ? totals.formatAsPrice(false, true, true) : totals.formatAsPrice(false, false, !["Refunds", "Scripts"].includes(dashboard.mainGraph));
                return name + ": " + formattedTotals;
            },
            bottom: 5,
            type: 'scroll',
            selected: dashboard.comparisonGraph.data == undefined || dashboard.comparisonGraph.data.length < 3? undefined  : //{}
               { [dashboard.comparisonGraph.data[0].name]: dashboard.comparisonGraph.data[0].show,  [dashboard.comparisonGraph.data[1].name]: dashboard.comparisonGraph.data[1].show,  [dashboard.comparisonGraph.data[2].name]: dashboard.comparisonGraph.data[2].show,  [dashboard.comparisonGraph.data[3].name]: dashboard.comparisonGraph.data[3].show}
            
            
            
            
           //- (): Dictionary<boolean> | undefined => {
           //     let help:Dictionary<boolean> = {};
           //     return help;

           //     return Dictionary<boolean>:{
            //        [dashboard.comparisonGraph.data[0].name]: dashboard.comparisonGraph.data[0].show
            //    };
            },
        tooltip: {
            trigger: 'item',
            axisPointer: {
                axis: "x",
            },
            valueFormatter(value, dataIndex) {
                if (value == null) return "N/A";
                const valueNumber = value as Number;
                return ["Sales", "Profit"].includes(dashboard.mainGraph) ? valueNumber.formatAsPrice(false, true, true) : valueNumber.formatAsPrice(false, false, !["Refunds", "Scripts"].includes(dashboard.mainGraph));
            }
        },
        toolbox: {
            feature: {
                magicType: {
                    type: ['line', 'bar'],
                    option: {
                        line: {
                            xAxis: {
                                type: 'category',
                                boundaryGap: false,
                            }
                        },
                    },
                    title: {
                        line: 'Switch to line chart',
                        bar: 'Switch to bar chart',
                    },
                },
                saveAsImage: {
                    title: 'Save',
                    name: 'Yearly Comparison' + "_" + new Date().toLocaleDateString(),
                },
            }
        }
    }), [dashboard.comparisonGraph.data]);

    const drillDownChartOptions: EChartsOption = useMemo<EChartsOption>(() => {
        if (drillDownParams == null) {
            return {};
        }

        // Get all unique groups for the data
        const uniqueGroups = dashboard.drillDownGraph.data.flatMap(d => d.graphData.flatMap(g => g.group)).filter(onlyUnique);

        return {
            color: eChartColours,
            grid: {
                containLabel: true,
                left: '3%',
                right: '3%',
                bottom: '4%',
                top: '5%',
                height: 'auto',
                width: 'auto',
            },
            animationDuration: 300,
            xAxis: {
                type: 'category',
                data: dashboard.drillDownGraph.data.map(p => p.pharmacy),
                axisPointer: {
                    show: true,
                    type: 'shadow'
                },
                axisLabel: {
                    interval: 0,
                    hideOverlap: true,
                },
                show: false
            },
            yAxis: {
                type: 'value',
                data: dashboard.drillDownGraph.data.map(p => p.graphData.map(d => d.data[drillDownParams!.dataIx])),
            },
            series: uniqueGroups.map((group) => {
                return {
                    type: 'bar',
                    stack: isStacked ? 'total' : null,
                    name: group,
                    label: {
                        show: true,
                        formatter: (params: any) => {
                            return params.value == null || params.value === 0 ? "" : params.value.formatAsPrice(false, ["Sales", "Profit"].includes(dashboard.mainGraph), ["Sales", "Profit", "Basket"].includes(dashboard.mainGraph));
                        }
                    },
                    labelLayout: {
                        hideOverlap: true,
                    },
                    data: dashboard.drillDownGraph.data.map(p => {
                        let data = p.graphData.find(g => g.group === group)?.data[drillDownParams!.dataIx];
                        return data != null ? Number.parseFloat(data.toFixed(2)) : undefined;
                    }),
                }
            }),
            legend: {
                data: uniqueGroups,
                formatter: (name: string) => {
                    // Format the legend label as params + : + total of the group
                    const groupData = dashboard.drillDownGraph.data.map(d => d.graphData.find(g => g.group === name)).filter(d => d != null);
                    const total = groupData.flatMap(d => d.data[drillDownParams!.dataIx]).filter(a => a !== null).reduce((a, b) => a + b, 0);
                    const formattedTotal = ["Sales", "Profit"].includes(dashboard.mainGraph) ? total?.formatAsPrice(false, true, true) : total?.formatAsPrice(false, false, !["Refunds", "Scripts"].includes(dashboard.mainGraph));
                    return name + ": " + formattedTotal;
                },
                bottom: '0%',
                type: 'scroll',
            },
            tooltip: {
                trigger: 'item',
                valueFormatter: (value: number) => {
                    if (value == null) return "N/A";
                    return ["Sales", "Profit"].includes(dashboard.mainGraph) ? value.formatAsPrice(false, true, true) : value.formatAsPrice(false, false, !["Refunds", "Scripts"].includes(dashboard.mainGraph));
                }
            },
            toolbox: {
                feature: {
                    myStack: {
                        title: isStacked ? 'Unstack' : 'Stack',
                        icon: 'M8.2,38.4l-8.4,4.1l30.6,15.3L60,42.5l-8.1-4.1l-21.5,11L8.2,38.4z M51.9,30l-8.1,4.2l-13.4,6.9l-13.9-6.9L8.2,30l-8.4,4.2l8.4,4.2l22.2,11l21.5-11l8.1-4.2L51.9,30z M51.9,21.7l-8.1,4.2L35.7,30l-5.3,2.8L24.9,30l-8.4-4.1l-8.3-4.2l-8.4,4.2L8.2,30l8.3,4.2l13.9,6.9l13.4-6.9l8.1-4.2l8.1-4.1L51.9,21.7zM30.4,2.2L-0.2,17.5l8.4,4.1l8.3,4.2l8.4,4.2l5.5,2.7l5.3-2.7l8.1-4.2l8.1-4.2l8.1-4.1L30.4,2.2z',
                        onclick: function () {
                            isStacked = !isStacked;
                            const chart = drilldownChartRef.current!.getEchartsInstance();
                            const seriesOption = chart.getModel().option.series.map(series => {return {stack: isStacked ? 'Total' : undefined}});
                            const toolboxOption = {feature: { myStack: { title: isStacked ? 'Unstack' : 'Stack', iconStyle: {borderColor: isStacked ? '#3E98C5' : '#666'}}}};
                            chart.setOption({series: seriesOption, toolbox: toolboxOption});
                        },
                        iconStyle: {borderColor: isStacked ? '#3E98C5' : '#666'}
                      },
                    saveAsImage: {
                        title: 'Save',
                        name: `${drillDownParams!.tab} by Type - Store Comparison View (${drillDownParams!.name} ${drillDownParams!.seriesName})` + "_" + new Date().toLocaleDateString(),
                    },
                }
            }
        } as EChartsOption;
    }, [drillDownParams]);
            

    function UpdateMainGraph(optionValue: any) {
        dispatch(updateMainGraph({
            mainGraph: optionValue
        }));
    }

    const navPills = [
        {
            key: "Sales",
            name: "Sales",
            icon: "mdi mdi-cart-plus",
        }, {
            key: "Scripts",
            name: "Scripts",
            icon: "mdi mdi-account-multiple",
        }, {
            key: "Profit",
            name: "Profit",
            icon: "mdi mdi-account-multiple",
        }, {
            key: "Basket",
            name: "Basket",
            icon: "mdi mdi-currency-usd",
        }, {
            key: "Refunds",
            name: "Refunds",
            icon: "mdi mdi-cash-refund",
        },
    ]

    type ClickEventParams = {
        seriesName: string;
        name: string;
        value: number;
        borderColor?: string;
        color: string;
        componentIndex: number;
        componentSubType: string;
        componentType: string;
        data: number;
        dataIndex: number;
        dataType?: string;
        seriesId: string;
        seriesIndex: number;
        seriesType: string;
        type: string;
    }

    const drillDown = (tab: string, storeId: number, month: number, year: number) => {
        let reportId = null;
        switch(tab) {
            case "Sales":
            case "Profit":
                reportId = ReportIds.Sales_By_Type;
                break;
            case "Scripts":
            case "Basket":
            case "Refunds":
                break;
        }

        if (reportId != null) {
            const startDate = formatISO(new Date(year, month, 1), { representation: 'date' });
            const endDate = formatISO(new Date(year, month + 1, 0), { representation: 'date' });
            setDrillDownOpen(false);
            navigate(`/app/ReportViewer/${reportId}?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}&generate=true`);
        }
    }
    
    const onEvents = {
        click: (params: ClickEventParams) => {
            const dataIx = 12 * params.seriesIndex + params.dataIndex;
            setDrillDownOpen(true);
            setDrillDownParams({
                name: params.name,
                seriesName: params.seriesName,
                value: params.value,
                tab: dashboard.mainGraph,
                dataIx: dataIx,
            });

            const drilldown = "YearlyComparison";
            const type = dashboard.mainGraph;
            const month = dataIx % 12;
            const year = params.seriesName;
            setSearchParams({ drillDown: drilldown, type: type, month: month.toString(), year: year });
        },
    };

    const chartInstanceRef = useRef(null);
    
    // ChatGPT wrote this to handle the click event on the chart and allow clicking the blank areas around the chart to trigger the drilldown
    const handleClick = useCallback(
        (params) => {
          if (!drilldownChartRef.current) {
            return;
          }
    
          const chartInstance = chartInstanceRef.current;
          const pointInPixel = [params.offsetX, params.offsetY];
    
          // Check if the click is within the grid area
          const isInGrid = chartInstance.containPixel({ gridIndex: 0 }, pointInPixel);
    
          // Determine if click is in the x-axis label area
          const gridBounding = chartInstance.getModel().getComponent('grid', 0).coordinateSystem.getRect();
          const isInXAxisLabelArea =
            pointInPixel[1] >= gridBounding.y + gridBounding.height &&
            pointInPixel[1] <= gridBounding.y + gridBounding.height + 50;
    
          if (!isInGrid && !isInXAxisLabelArea) {
            return;
          }
    
          const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
    
          const xAxisIndex = Math.round(pointInGrid[0]);
          const xAxis = chartInstance.getOption().xAxis[0];
    
          if (xAxisIndex >= 0 && xAxisIndex < xAxis.data.length) {
            const category = xAxis.data[xAxisIndex];
    
            // Navigate to the store report
            const store = dashboard.drillDownGraph.data[xAxisIndex];
            const month = drillDownParams.dataIx % 12;
            const tab = drillDownParams.tab;
            const year = parseInt(drillDownParams.seriesName);

            console.log("Clicked on store", store);
    
            drillDown(tab, store.id, month, year);
          }
        },
        [drillDownOpen, drillDownParams, stores, drillDown]
      );
    
      const onChartReady = useCallback((chartInstance) => {
        chartInstanceRef.current = chartInstance; // Store the chart instance for later use
      }, []);
    
      // More ChatGPT code to attach the click event listener to the chart
      useEffect(() => {
        if (chartInstanceRef.current && drillDownOpen) {
          const chartInstance = chartInstanceRef.current;
          const zr = chartInstance.getZr();
    
          if (!zr) {
            return;
          }
    
          // Attach the click event listener
          zr.on('click', handleClick);
    
          // Cleanup on component unmount or dependency change
          return () => {
            if (chartInstance) {
              const zr = chartInstance.getZr();
              if (zr) {
                zr.off('click', handleClick);
              }
            }
          };
        }
      }, [drillDownOpen, handleClick]);

    useEffect(() => {
        if (searchParams.has("drillDown") && !dashboard.loading) {
            const drilldown = searchParams.get("drillDown");
            if (drilldown === "YearlyComparison") {
                const type = searchParams.get("type");
                const monthIx = searchParams.get("month");
                const year = searchParams.get("year");

                if (type && monthIx && year) {
                    const yearIx = years.findIndex((y) => y === year);
                    const dataIx = 12 * yearIx + parseInt(monthIx);
                    UpdateMainGraph(type);
                    setDrillDownParams(
                        {
                            name: format(new Date(0, parseInt(monthIx)), "MMM"),
                            seriesName: year,
                            value: 0,
                            tab: type,
                            dataIx: dataIx,
                        }
                    );
                    setDrillDownOpen(true);
                }
            } else {
                if (drillDownOpen) setDrillDownOpen(false);
            }
        } else {
            if (drillDownOpen) setDrillDownOpen(false);
        }
    }, [searchParams, dashboard.loading]);
      

    return (
        <>
        <Modal centered size="xl" show={drillDownOpen} onHide={() => {
            searchParams.delete("drillDown");
            searchParams.delete("type");
            searchParams.delete("month");
            searchParams.delete("year");
            setSearchParams(searchParams);
            setDrillDownOpen(false)}
        }>
            <Modal.Header closeButton>
                <Modal.Title>{drillDownParams?.tab} by Type - Store Comparison View ({drillDownParams?.name} {drillDownParams?.seriesName})</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column" style={{
                minHeight: "80dvh",
            }}>
                <EChartsReact
                    ref={drilldownChartRef}
                    option={drillDownChartOptions}
                    onChartReady={onChartReady}
                    className="flex-grow"
                />
            </Modal.Body>
        </Modal>
        <Card style={{ marginBottom: "0px", height: '100%' }}>
            <Card.Body className="ps-2 d-flex flex-column">
                <Row className="mb-1">
                    <Col xs={12} sm={6}>
                        <h4 className="dasboard-widget-title mb-0 d-inline-flex gap-1">Yearly Comparison <i className="widget-icon mdi mdi-chart-areaspline" /><ZTooltip text="Exclusive of this month"><ZIcon icon={ZIconType.Information} size="14px"/></ZTooltip></h4>
                    </Col>
                    <Col xs={12} sm={6} className="pe-0" style={{ paddingLeft: "18px" }}>
                        <Dropdown className="page-title-middle d-md-none" style={{ paddingLeft: "6px" }}>
                            <Dropdown.Toggle id="dropdown-basic2" variant="primary" style={{ borderRadius: "5px", fontSize: `var(--bs-nav-link-font-size)` }}>
                                {dashboard.mainGraph}
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ width: "80px" }}>
                                {navPills.map((tab, index) => (
                                    <Dropdown.Item
                                        key={tab.key}
                                        id={tab.key.toString()}
                                        active={dashboard.mainGraph === tab.key}
                                        onClick={() => UpdateMainGraph(tab.key)}
                                    >{tab.name}</Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <div className="page-title-right dashboard-Revenue-Chart-tab hide-when-medium">
                            <Tab.Container defaultActiveKey={dashboard.mainGraph} >
                                <Nav variant="pills" justify className="bg-nav-pills">
                                    {navPills.map((tab, index) => {
                                        return (
                                            <Nav.Item key={index} onClick={() => UpdateMainGraph(tab.key)}>
                                                <Nav.Link eventKey={tab.key} ><span className="d-md-block">{tab.name}</span></Nav.Link>
                                            </Nav.Item>
                                        );
                                    })}
                                </Nav>
                            </Tab.Container>
                        </div>
                    </Col>
                </Row>
                <Row className="d-flex flex-grow pt-1" style={{ minHeight: "400px" }}>
                    <ReactECharts
                        option={chartOptions}
                        onEvents={onEvents}
                        className="h-100 w-100"
                    />
                </Row>
            </Card.Body>
        </Card>
        </>
    );
};

export default TotalRevenueChart;
